import { useState, useEffect } from "react";
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { useToast } from "../../util/ToastProvider";
import PasswordFormUI from "../../components/PasswordFormUI"; // adjust path

export default function ChangePasswordComponent() {
  const auth = getAuth();
  const toast = useToast();

  const [oobCode, setOobCode] = useState("");
  const [validCode, setValidCode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("oobCode");

    if (!code) {
      setError("Invalid link");
      setLoading(false);
      return;
    }

    setOobCode(code);

    verifyPasswordResetCode(auth, code)
      .then(() => setValidCode(true))
      .catch(() => {
        setError("Invalid or expired link");
        setValidCode(false);
      })
      .finally(() => setLoading(false));
  }, [auth]);

  const validatePassword = (pwd: string) => {
    const regex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-={}\[\]|;:'",.<>/?]).{8,}$/;
    return regex.test(pwd);
  };

  const handleSubmit = async (password: string, confirmPassword: string) => {
    setError("");

    if (!validatePassword(password)) {
      setError(
        "Password must contain at least 8 characters, including one uppercase letter, one number, and one special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      toast.success("Password reset successfully. You can now sign in.");
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // NEW: prevent flicker
  if (loading) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 border rounded-lg bg-slate-50">
        <h2 className="text-xl font-bold text-slate-700 mb-4">Verifying link…</h2>
        <p className="text-slate-600">Please wait.</p>
      </div>
    );
  }

  if (success)
    return (
      <div className="max-w-md mx-auto mt-10 p-6 border rounded-lg bg-green-50">
        <h2 className="text-xl font-bold text-green-700 mb-4">
          Password Reset!
        </h2>
        <p className="text-green-800">
          Your password has been updated. You can now{" "}
          <a
            href="/auth/login"
            className="text-blue-600 font-medium underline hover:text-blue-800"
          >
            sign in
          </a>.
        </p>
      </div>
    );

  if (!validCode)
    return (
      <div className="max-w-md mx-auto mt-10 p-6 border rounded-lg bg-rose-50">
        <h2 className="text-xl font-bold text-rose-700 mb-4">Invalid Link</h2>
        <p>The password reset link is invalid or expired.</p>
      </div>
    );

  return (
    <PasswordFormUI
      title="Reset Your Password"
      buttonLabel="Reset Password"
      loading={loading}
      error={error}
      onSubmit={handleSubmit}
    />
  );
}

